import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  QrCode, MessageSquare, Shield, Star, Bell, Globe, Zap, Wifi,
  CheckCircle, ArrowRight, ChevronDown, XCircle, UserX, TrendingDown,
  Download, Smartphone, Share2, Menu, X, Monitor
} from 'lucide-react';
import { useLanguage, languageNames } from './LanguageContext';
import './LandingPage.css';

const HERO_IMG = "https://images.pexels.com/photos/6205512/pexels-photo-6205512.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BUSINESS_IMG = "https://images.pexels.com/photos/6205471/pexels-photo-6205471.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SucoLogoSmall = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_44d3bbc8-743b-49ac-9ae6-748329023b48/artifacts/tpio1cnh_1000493039.png" 
    alt="SUCO" 
    width="36" 
    height="36" 
    style={{ borderRadius: '50%', objectFit: 'cover' }}
  />
);

const PromoLandingPage = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const deferredPrompt = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // If opened as installed PWA, redirect to dashboard
    if (window.matchMedia('(display-mode: standalone)').matches) {
      const hasSession = localStorage.getItem('suco_business');
      navigate(hasSession ? '/dashboard' : '/login', { replace: true });
      return;
    }

    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);

    // Detect iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    // Capture install prompt (Android/Chrome)
    const handlePrompt = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handlePrompt);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeinstallprompt', handlePrompt);
    };
  }, [navigate]);

  const handleInstallClick = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      const result = await deferredPrompt.current.userChoice;
      if (result.outcome === 'accepted') {
        setInstalled(true);
        setCanInstall(false);
      }
      deferredPrompt.current = null;
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
    <div className="landing-page" data-testid="promo-landing-page">
      {/* Navbar */}
      <nav className={`lp-navbar ${scrolled ? 'scrolled' : ''}`} data-testid="lp-navbar">
        <div className="lp-navbar-inner">
          <Link to="/" className="lp-navbar-brand">
            <SucoLogoSmall />
            <span>SUCO</span>
          </Link>

          {/* Desktop menu */}
          <div className="lp-navbar-actions lp-desktop-menu">
            <button className="lp-btn-ghost" onClick={() => scrollToSection('como-funciona')} data-testid="nav-how-it-works">
              {t('lp_nav_how')}
            </button>
            <button className="lp-btn-ghost" onClick={() => scrollToSection('precios')} data-testid="nav-pricing">
              {t('lp_nav_pricing')}
            </button>
            <button className="lp-btn-ghost" onClick={() => scrollToSection('descargar')} data-testid="nav-download">
              {t('lp_nav_download')}
            </button>
            <Link to="/login" className="lp-btn-ghost" data-testid="nav-login">
              {t('lp_nav_login')}
            </Link>

            {/* Language selector */}
            <div className="lp-lang-selector" data-testid="lang-selector">
              <button className="lp-btn-ghost lp-lang-btn" onClick={() => setLangMenuOpen(!langMenuOpen)}>
                <Globe size={16} /> {languageNames[language]}
              </button>
              {langMenuOpen && (
                <div className="lp-lang-dropdown" data-testid="lang-dropdown">
                  {Object.entries(languageNames).map(([code, name]) => (
                    <button key={code} className={`lp-lang-option ${code === language ? 'active' : ''}`} onClick={() => { setLanguage(code); setLangMenuOpen(false); }}>
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link to="/registro" className="lp-btn-primary" data-testid="nav-cta">
              {t('lp_nav_cta')}
            </Link>
          </div>

          {/* Mobile hamburger button */}
          <button className="lp-mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn" aria-label="Menu">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="lp-mobile-menu" data-testid="mobile-menu">
            <button className="lp-mobile-menu-item" onClick={() => { scrollToSection('como-funciona'); setMobileMenuOpen(false); }}>
              {t('lp_nav_how')}
            </button>
            <button className="lp-mobile-menu-item" onClick={() => { scrollToSection('precios'); setMobileMenuOpen(false); }}>
              {t('lp_nav_pricing')}
            </button>
            <button className="lp-mobile-menu-item" onClick={() => { scrollToSection('descargar'); setMobileMenuOpen(false); }} data-testid="mobile-nav-download">
              {t('lp_nav_download')}
            </button>
            <Link to="/login" className="lp-mobile-menu-item" onClick={() => setMobileMenuOpen(false)}>
              {t('lp_nav_login')}
            </Link>
            {/* Mobile language selector */}
            <div className="lp-mobile-lang">
              <Globe size={16} />
              <select value={language} onChange={(e) => setLanguage(e.target.value)} className="lp-mobile-lang-select" data-testid="mobile-lang-select">
                {Object.entries(languageNames).map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>
            <Link to="/registro" className="lp-mobile-menu-item lp-mobile-menu-cta" onClick={() => setMobileMenuOpen(false)}>
              {t('lp_nav_cta')}
            </Link>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="lp-hero" data-testid="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-text">
            <h1>
              {t('lp_hero_title')} <span className="highlight">{t('lp_hero_highlight')}</span>
            </h1>
            <p>{t('lp_hero_desc')}</p>
            <div className="lp-hero-actions">
              <Link to="/registro" className="lp-btn-primary lp-btn-primary-lg" data-testid="hero-cta">
                {t('lp_hero_cta')} <ArrowRight size={20} />
              </Link>
              <button className="lp-btn-secondary" onClick={() => scrollToSection('como-funciona')} data-testid="hero-how">
                <ChevronDown size={20} /> {t('lp_hero_how')}
              </button>
            </div>
          </div>
          <div className="lp-hero-image">
            <img src={HERO_IMG} alt="SUCO" loading="lazy" />
            <div className="lp-hero-badge">
              <CheckCircle size={20} />
              {t('lp_hero_badge')}
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="lp-problem" data-testid="lp-problem">
        <div className="lp-problem-inner">
          <span className="lp-section-label">{t('lp_problem_label')}</span>
          <h2>{t('lp_problem_title')}</h2>
          <div className="lp-problem-stat">96%</div>
          <p className="lp-problem-stat-label">{t('lp_problem_stat_text')}</p>
          <div className="lp-problem-cards">
            <div className="lp-problem-card">
              <div className="icon"><UserX size={24} /></div>
              <h4>{t('lp_problem_card1_title')}</h4>
              <p>{t('lp_problem_card1_desc')}</p>
            </div>
            <div className="lp-problem-card">
              <div className="icon"><XCircle size={24} /></div>
              <h4>{t('lp_problem_card2_title')}</h4>
              <p>{t('lp_problem_card2_desc')}</p>
            </div>
            <div className="lp-problem-card">
              <div className="icon"><TrendingDown size={24} /></div>
              <h4>{t('lp_problem_card3_title')}</h4>
              <p>{t('lp_problem_card3_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution / How it works */}
      <section className="lp-solution" id="como-funciona" data-testid="lp-solution">
        <div className="lp-solution-inner">
          <span className="lp-section-label blue">{t('lp_solution_label')}</span>
          <h2>{t('lp_solution_title')}</h2>
          <p style={{ fontSize: '1.1rem', color: '#475569', maxWidth: 600, margin: '0 auto 48px' }}>
            {t('lp_solution_subtitle')}
          </p>
          <div className="lp-steps">
            <div className="lp-step">
              <div className="step-icon"><QrCode size={28} /></div>
              <div className="lp-step-number">1</div>
              <h3>{t('lp_step1_title')}</h3>
              <p>{t('lp_step1_desc')}</p>
            </div>
            <div className="lp-step">
              <div className="step-icon"><MessageSquare size={28} /></div>
              <div className="lp-step-number">2</div>
              <h3>{t('lp_step2_title')}</h3>
              <p>{t('lp_step2_desc')}</p>
            </div>
            <div className="lp-step">
              <div className="step-icon"><Bell size={28} /></div>
              <div className="lp-step-number">3</div>
              <h3>{t('lp_step3_title')}</h3>
              <p>{t('lp_step3_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="lp-benefits" data-testid="lp-benefits">
        <div className="lp-benefits-inner">
          <span className="lp-section-label green">{t('lp_benefits_label')}</span>
          <h2>{t('lp_benefits_title')}</h2>
          <div className="lp-benefits-grid">
            <div className="lp-benefit-card">
              <div className="lp-benefit-icon"><Shield size={22} /></div>
              <div>
                <h4>{t('lp_benefit1_title')}</h4>
                <p>{t('lp_benefit1_desc')}</p>
              </div>
            </div>
            <div className="lp-benefit-card">
              <div className="lp-benefit-icon"><Star size={22} /></div>
              <div>
                <h4>{t('lp_benefit2_title')}</h4>
                <p>{t('lp_benefit2_desc')}</p>
              </div>
            </div>
            <div className="lp-benefit-card">
              <div className="lp-benefit-icon"><TrendingDown size={22} style={{ transform: 'rotate(180deg)' }} /></div>
              <div>
                <h4>{t('lp_benefit3_title')}</h4>
                <p>{t('lp_benefit3_desc')}</p>
              </div>
            </div>
            <div className="lp-benefit-card">
              <div className="lp-benefit-icon"><Zap size={22} /></div>
              <div>
                <h4>{t('lp_benefit4_title')}</h4>
                <p>{t('lp_benefit4_desc')}</p>
              </div>
            </div>
            <div className="lp-benefit-card">
              <div className="lp-benefit-icon"><Globe size={22} /></div>
              <div>
                <h4>{t('lp_benefit5_title')}</h4>
                <p>{t('lp_benefit5_desc')}</p>
              </div>
            </div>
            <div className="lp-benefit-card">
              <div className="lp-benefit-icon"><QrCode size={22} /></div>
              <div>
                <h4>{t('lp_benefit6_title')}</h4>
                <p>{t('lp_benefit6_desc')}</p>
              </div>
            </div>
            <div className="lp-benefit-card lp-benefit-new">
              <div className="lp-benefit-icon"><Wifi size={22} /></div>
              <div>
                <span className="lp-new-badge">{t('lp_new')}</span>
                <h4>{t('lp_wifi_title')}</h4>
                <p>{t('lp_wifi_desc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="lp-pricing" id="precios" data-testid="lp-pricing">
        <div className="lp-pricing-inner">
          <span className="lp-section-label blue">{t('lp_pricing_label')}</span>
          <h2>{t('lp_pricing_title')}</h2>
          <p style={{ color: '#475569', marginBottom: 48, fontSize: '1.05rem' }}>{t('lp_pricing_subtitle')}</p>

          <h3 style={{ fontSize: '1.1rem', color: '#475569', marginBottom: 20, fontWeight: 600 }}>{t('lp_pricing_business')}</h3>
          <div className="lp-pricing-cards">
            <div className="lp-pricing-card" data-testid="pricing-monthly">
              <h3>{t('lp_pricing_monthly')}</h3>
              <div>
                <span className="lp-pricing-amount">$12.99</span>
                <span className="lp-pricing-period"> USD/{t('per_month')}</span>
              </div>
              <ul className="lp-pricing-features">
                <li><CheckCircle size={18} /> {t('lp_pricing_f1')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_f2')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_f3')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_f4')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_f5')}</li>
              </ul>
              <Link to="/registro" className="lp-btn-primary" style={{ width: '100%', justifyContent: 'center' }} data-testid="pricing-monthly-cta">
                {t('lp_hero_cta')}
              </Link>
            </div>
            <div className="lp-pricing-card featured" data-testid="pricing-yearly">
              <div className="lp-pricing-popular">{t('lp_pricing_save')}</div>
              <h3>{t('lp_pricing_yearly')}</h3>
              <div>
                <span className="lp-pricing-amount">$110</span>
                <span className="lp-pricing-period"> USD/{t('per_year')}</span>
              </div>
              <ul className="lp-pricing-features">
                <li><CheckCircle size={18} /> {t('lp_pricing_yf1')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_yf2')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_yf3')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_yf4')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_yf5')}</li>
              </ul>
              <Link to="/registro" className="lp-btn-primary" style={{ width: '100%', justifyContent: 'center' }} data-testid="pricing-yearly-cta">
                {t('lp_hero_cta')}
              </Link>
            </div>
          </div>

          <div className="lp-enterprise-section" data-testid="pricing-enterprise">
            <h3 style={{ fontSize: '1.1rem', color: '#475569', marginTop: 48, marginBottom: 20, fontWeight: 600 }}>{t('lp_pricing_enterprise_label')}</h3>
            <div className="lp-pricing-card featured" style={{ maxWidth: 500, margin: '0 auto' }}>
              <div className="lp-pricing-popular">{t('lp_pricing_enterprise_badge')}</div>
              <h3>{t('lp_pricing_enterprise')}</h3>
              <div>
                <span className="lp-pricing-amount">$29.99</span>
                <span className="lp-pricing-period"> USD/{t('per_month')}</span>
              </div>
              <p style={{ color: '#64748B', fontSize: '0.9rem', margin: '8px 0' }}>{t('lp_pricing_enterprise_extra')}</p>
              <ul className="lp-pricing-features">
                <li><CheckCircle size={18} /> {t('lp_pricing_ef1')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_ef2')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_ef3')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_ef4')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_ef5')}</li>
                <li><CheckCircle size={18} /> {t('lp_pricing_ef6')}</li>
              </ul>
              <div style={{ background: '#F0F9FF', borderRadius: 12, padding: '14px 18px', marginBottom: 16 }}>
                <p style={{ fontSize: '0.85rem', color: '#1E40AF', fontWeight: 600 }}>{t('lp_pricing_example')}</p>
              </div>
              <Link to="/registro" className="lp-btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                {t('lp_pricing_contact')}
              </Link>
            </div>
          </div>

          <p className="lp-pricing-note">{t('lp_pricing_trial')}</p>
        </div>
      </section>

      {/* Trust */}
      <section className="lp-trust" data-testid="lp-trust">
        <div className="lp-trust-inner">
          <h3>{t('lp_trust_title')}</h3>
          <p>{t('lp_trust_desc')}</p>
          <div className="lp-trust-types">
            {t('lp_trust_tags').split(',').map((tag) => (
              <span key={tag.trim()} className="lp-trust-tag">{tag}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Download App */}
      {!installed && (
        <section id="descargar" className="lp-download" data-testid="lp-download">
          <div className="lp-download-inner">
            <Smartphone size={48} className="lp-download-icon" />
            <h2>{t('lp_download_title')}</h2>
            <p>{t('lp_download_desc')}</p>
            
            {canInstall && (
              <button className="lp-btn-download" onClick={handleInstallClick} data-testid="install-app-btn">
                <Download size={22} />
                {t('lp_download_btn')}
              </button>
            )}

            {!canInstall && (
              <div className="lp-install-methods">
                <div className="lp-install-card lp-install-desktop" data-testid="install-desktop">
                  <div className="lp-install-card-header">
                    <Monitor size={24} />
                    <strong>{t('lp_download_desktop')}</strong>
                  </div>
                  <div className="lp-ios-steps">
                    <div className="lp-ios-step"><span className="lp-ios-step-num">1</span><span>{t('lp_download_d1')}</span></div>
                    <div className="lp-ios-step"><span className="lp-ios-step-num">2</span><span>{t('lp_download_d2')}</span></div>
                    <div className="lp-ios-step"><span className="lp-ios-step-num">3</span><span>{t('lp_download_d3')}</span></div>
                  </div>
                </div>
                <div className="lp-install-card" data-testid="install-android">
                  <div className="lp-install-card-header">
                    <Smartphone size={24} />
                    <strong>{t('lp_download_android')}</strong>
                  </div>
                  <div className="lp-ios-steps">
                    <div className="lp-ios-step"><span className="lp-ios-step-num">1</span><span>{t('lp_download_a1')}</span></div>
                    <div className="lp-ios-step"><span className="lp-ios-step-num">2</span><span>{t('lp_download_a2')}</span></div>
                    <div className="lp-ios-step"><span className="lp-ios-step-num">3</span><span>{t('lp_download_a3')}</span></div>
                  </div>
                </div>
                <div className="lp-install-card" data-testid="install-ios">
                  <div className="lp-install-card-header">
                    <Share2 size={24} />
                    <strong>{t('lp_download_ios')}</strong>
                  </div>
                  <div className="lp-ios-steps">
                    <div className="lp-ios-step"><span className="lp-ios-step-num">1</span><span>{t('lp_download_i1')}</span></div>
                    <div className="lp-ios-step"><span className="lp-ios-step-num">2</span><span>{t('lp_download_i2')}</span></div>
                    <div className="lp-ios-step"><span className="lp-ios-step-num">3</span><span>{t('lp_download_i3')}</span></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="lp-cta" data-testid="lp-cta">
        <div className="lp-cta-inner">
          <h2>{t('lp_cta_title')}</h2>
          <p>{t('lp_cta_desc')}</p>
          <Link to="/registro" className="lp-btn-cta" data-testid="final-cta">
            {t('lp_cta_btn')} <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <p>&copy; {new Date().getFullYear()} SUCO - {t('lp_footer')}</p>
        <a href="mailto:info@sucomx.com" className="lp-footer-email" data-testid="footer-support-email">info@sucomx.com</a>
        <div className="lp-footer-legal">
          <Link to="/privacy" data-testid="footer-privacy-link">Política de Privacidad</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms" data-testid="footer-terms-link">Términos y Condiciones</Link>
        </div>
      </footer>
    </div>

      {/* WhatsApp floating button */}
      <a
        href="https://wa.me/526633224300?text=Hola%2C%20me%20interesa%20conocer%20m%C3%A1s%20sobre%20SUCO%20para%20mi%20negocio"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-btn"
        aria-label="WhatsApp"
        style={{
          position: 'fixed',
          bottom: '80px',
          right: '24px',
          width: '60px',
          height: '60px',
          background: '#25D366',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(37, 211, 102, 0.4)',
          zIndex: 9999,
          textDecoration: 'none'
        }}
      >
        <svg viewBox="0 0 32 32" width="28" height="28" fill="#fff"><path d="M16.004 0C7.168 0 .004 7.163.004 16c0 2.822.737 5.574 2.137 7.998L.008 32l8.187-2.11A15.94 15.94 0 0016.004 32C24.84 32 32 24.837 32 16S24.84 0 16.004 0zm0 29.18a13.14 13.14 0 01-7.084-2.065l-.508-.302-5.27 1.358 1.406-5.125-.33-.526A13.12 13.12 0 012.83 16c0-7.27 5.916-13.18 13.174-13.18 7.265 0 13.176 5.91 13.176 13.18 0 7.27-5.91 13.18-13.176 13.18zm7.225-9.87c-.396-.198-2.34-1.153-2.703-1.284-.363-.132-.627-.198-.891.198-.264.396-1.023 1.284-1.254 1.548-.231.264-.462.297-.858.099-.396-.198-1.672-.615-3.186-1.962-1.177-1.05-1.971-2.346-2.202-2.742-.231-.396-.025-.61.173-.808.178-.178.396-.462.594-.693.198-.231.264-.396.396-.66.132-.264.066-.495-.033-.693-.099-.198-.891-2.148-1.221-2.94-.321-.771-.648-.666-.891-.678l-.759-.013c-.264 0-.693.099-1.056.495-.363.396-1.386 1.353-1.386 3.3 0 1.947 1.419 3.828 1.617 4.092.198.264 2.793 4.26 6.768 5.976.945.408 1.683.651 2.259.834.948.3 1.812.258 2.493.156.762-.114 2.34-.957 2.67-1.881.33-.924.33-1.716.231-1.881-.099-.165-.363-.264-.759-.462z"/></svg>
      </a>
    </>
  );
};

export default PromoLandingPage;
